import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { IncidentSeverity, IncidentSeverityLabelKeys } from '../../../enums/incident-severity.enum';
import { IncidentReport } from '../../../models/accidents-and-incidents-response.model';
import { ACCIDENTS_DATE_TIME_FORMAT } from '../../../constants/accidents-and-incidents.constants';
import { LookupIncidentType } from '../../../../../_shared/models/lookup.model';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { AccidentsAndIncidentsService } from '../../../services/accidents-and-incidents.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';

@Component({
  selector: 'app-edit-incident-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './edit-incident-modal.component.html',
  styleUrl: './edit-incident-modal.component.scss'
})
export class EditIncidentModalComponent implements OnInit, OnDestroy {
  @Input() incident: IncidentReport | null = null;

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private lookupService: LookupService = inject(LookupService);
  private accidentsAndIncidentsService: AccidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  readonly severities: IncidentSeverity[] = [IncidentSeverity.LowImpact, IncidentSeverity.MediumImpact, IncidentSeverity.HighImpact];
  readonly severityLabelKeys = IncidentSeverityLabelKeys;
  private readonly dateTimeLocale = 'en-ZA';

  editForm: FormGroup;
  incidentTypes: LookupIncidentType[] = [];
  selectedIncidentType: LookupIncidentType | null = null;
  selectedSeverity: IncidentSeverity | null = null;
  isLoadingIncidentTypes: boolean = false;
  isSaving: boolean = false;
  private initialIncidentTypeId: number | null = null;
  private initialSeverity: IncidentSeverity | null = null;
  private initialDescription: string = '';
  private cancelSubject = new Subject<void>();

  constructor() {
    this.editForm = this.fb.group({
      assetName: [{ value: '', disabled: true }],
      driver: [{ value: '', disabled: true }],
      location: [{ value: '', disabled: true }],
      dateTime: [{ value: '', disabled: true }],
      description: [''],
      witnessDetails: [{ value: '', disabled: true }]
    });
  }

  get incidentTypeLabel(): string {
    return this.selectedIncidentType?.name
      ? (this.selectedIncidentType.localizedName || this.selectedIncidentType.name)
      : this.transloco.translate('accidents.modals.editIncident.selectIncidentType');
  }

  get severityLabel(): string {
    return this.transloco.translate(
      this.selectedSeverity ? this.severityLabelKeys[this.selectedSeverity] : 'accidents.modals.editIncident.selectSeverity'
    );
  }

  get isFormValid(): boolean {
    return !!(this.incident?.incidentId && this.incident?.assetId && this.selectedIncidentType && this.selectedSeverity);
  }

  get hasChanges(): boolean {
    const currentDescription = this.editForm.get('description')?.value || '';
    return (
      this.selectedIncidentType?.id !== this.initialIncidentTypeId ||
      this.selectedSeverity !== this.initialSeverity ||
      currentDescription !== this.initialDescription
    );
  }

  get canSave(): boolean {
    return this.isFormValid && this.hasChanges && !this.isSaving;
  }

  async ngOnInit(): Promise<void> {
    if (this.incident) {
      this.editForm.patchValue({
        assetName: this.incident.assetName,
        driver: this.incident.driverInvolved,
        location: this.incident.location,
        dateTime: this.formatIncidentDateTime(this.incident.occurredAt),
        description: this.incident.description,
        witnessDetails: this.incident.witnessDetails
      });
      this.selectedSeverity = this.incident.severityType;

      this.initialIncidentTypeId = this.incident.incidentTypeId;
      this.initialSeverity = this.incident.severityType;
      this.initialDescription = this.incident.description || '';
    }

    await this.loadIncidentTypes();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onIncidentTypeSelect(type: LookupIncidentType): void {
    this.selectedIncidentType = type;
  }

  onSeveritySelect(severity: IncidentSeverity): void {
    this.selectedSeverity = severity;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.canSave || !this.incident || !this.selectedIncidentType || !this.selectedSeverity) {
      return;
    }

    this.isSaving = true;

    try {
      await this.accidentsAndIncidentsService.upsertIncident({
        incidentId: this.incident.incidentId,
        assetId: this.incident.assetId,
        incidentType: this.selectedIncidentType.id,
        severityType: this.selectedSeverity,
        driverInvolved: this.incident.driverInvolved,
        locationText: this.incident.location,
        description: this.editForm.get('description')?.value || null,
        witnessDetails: this.incident.witnessDetails || null,
        latitude: this.incident.latitude,
        longitude: this.incident.longitude,
        occurredAt: this.incident.occurredAt,
        attachments: []
      }, this.cancelSubject);

      this.activeModal.close(true);
    } catch (error) {
      this.isSaving = false;
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: () => this.toastr.error(this.transloco.translate('accidents.modals.editIncident.errors.saveFailed'))
      });
    }
  }

  private formatIncidentDateTime(value: string | Date): string {
    return value ? formatDate(value, ACCIDENTS_DATE_TIME_FORMAT, this.dateTimeLocale) : '';
  }

  private async loadIncidentTypes(): Promise<void> {
    this.isLoadingIncidentTypes = true;

    try {
      const response = await this.lookupService.getIncidentTypesAsync(this.cancelSubject);
      this.incidentTypes = response.data ?? [];
      this.selectedIncidentType = this.resolveIncidentType();
      this.isLoadingIncidentTypes = false;
    } catch (error) {
      this.incidentTypes = [];
      this.isLoadingIncidentTypes = false;
      this.errorHandlerService.handleError(error, { showToast: false });
    }
  }

  private resolveIncidentType(): LookupIncidentType | null {
    if (!this.incident) {
      return null;
    }

    return this.incidentTypes.find((type) => type.id === this.incident?.incidentTypeId)
      ?? this.incidentTypes.find((type) => type.name.toLowerCase() === this.incident?.incidentType.toLowerCase())
      ?? null;
  }
}
